import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, of, pipe, throwError } from 'rxjs';
import { catchError, concatMap, filter, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, CustomerService } from '../services';
import { CustomerState, initialCustomerState } from '../models/state/customer.state';
import { Customer, CustomerUpdateRequest, PageRequest } from '../models';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CustomerCreateRequest } from '../models/customer-create-request';

export const CustomerStore = signalStore(
  { providedIn: 'root' },
  withState<CustomerState>(initialCustomerState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      customerService = inject(CustomerService),
      toastr = inject(ToastrService),
      translate = inject(TranslateService),
    ) => {
      const loadCustomers = rxMethod<PageRequest>(
        pipe(
          map((_pageRequest) => {
            const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
            patchState(store, { loading: true, pageRequest });
            return pageRequest;
          }),
          switchMap((pageRequest) =>
            customerService.getCustomersByRole(pageRequest).pipe(
              tap((customers) =>
                patchState(store, {
                  customers,
                  completed: customers.length < pageRequest.size,
                }),
              ),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );
      const loadMoreCustomers = rxMethod<void>(
        pipe(
          filter(() => !store.completed()),
          map(() => {
            const { page, ...restOfPageRequest } = store.pageRequest();
            const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { pageRequest });
            return pageRequest;
          }),
          concatMap((pageRequest) =>
            customerService.getCustomersByRole(pageRequest).pipe(
              tap((customers) =>
                patchState(store, {
                  customers: [...store.customers(), ...customers],
                  completed: customers.length < (pageRequest.size ?? 0),
                }),
              ),
              catchError(() => of()), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      function createCustomer(data: CustomerCreateRequest): Observable<Customer> {
        patchState(store, { saving: true });

        return customerService.adminCreateCustomer(data).pipe(
          tap(() => {
            loadCustomers(store.pageRequest());
            toastr.success(translate.instant('customers.alerts.createSuccess'));
          }),
          catchError((error) => {
            toastr.error(translate.instant('customers.alerts.createFail'));
            return throwError(() => error);
          }),
          finalize(() => patchState(store, { saving: false })),
        );
      }

      function updateCustomer(id: number, customerData: CustomerUpdateRequest): Observable<Customer> {
        patchState(store, { saving: true });

        return customerService.adminUpdateCustomer(id, customerData).pipe(
          tap(() => {
            loadCustomers(store.pageRequest());
            toastr.success(translate.instant('customers.alerts.updateSuccess'));
          }),
          catchError((error) => {
            toastr.error(translate.instant('customers.alerts.updateFail'));
            return throwError(() => error);
          }),
          finalize(() => patchState(store, { saving: false })),
        );
      }

      function deleteCustomer(customerId: number): Observable<void> {
        patchState(store, { saving: true });

        return customerService.deleteCustomer(customerId).pipe(
          tap(() => {
            patchState(store, {
              customers: [...store.customers().filter((_customer) => _customer.id !== customerId)],
            });
            toastr.success(translate.instant('customers.alerts.deleteSuccess'));
          }),
          catchError((error) => {
            toastr.error(
              translate.instant(error.status === 403 ? 'common.alerts.permissionDenied' : 'common.alerts.genericError'),
            );
            return throwError(() => error);
          }),
          finalize(() => patchState(store, { saving: false })),
        );
      }

      const loadExternalNames = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { externalNamesLoading: true })),
          switchMap(() =>
            customerService.getExternalNames().pipe(
              tap((names) => patchState(store, { externalNames: names })),
              catchError(() => {
                patchState(store, { externalNames: [] });
                return of();
              }),
              finalize(() => patchState(store, { externalNamesLoading: false })),
            ),
          ),
        ),
      );
      return {
        createCustomer,
        updateCustomer,
        deleteCustomer,
        loadCustomers,
        loadMoreCustomers,
        loadExternalNames,
      };
    },
  ),
);
